import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { ErrorNote, SectionTitle, Spinner } from '../../components/ui';

interface Risk {
  id: string;
  use_case_id: string | null;
  use_case_name: string | null;
  risk_type: string | null;
  description: string;
  likelihood: number;
  impact: number;
  treatment: string;
  owner: string | null;
  residual_risk: string | null;
  review_date: string | null;
  status: string;
}

const RISK_TYPES = ['hallucination/quality', 'privacy/data', 'security', 'bias/fairness', 'regulatory/legal', 'operational', 'vendor/third-party', 'reputational'];
const TREATMENTS = ['mitigate', 'avoid', 'transfer', 'accept'];
const STATUSES = ['open', 'treated', 'closed'];

function sevColor(score: number): string {
  if (score >= 16) return 'bg-red-900/70 text-red-200 border-red-700';
  if (score >= 9) return 'bg-amber-900/60 text-amber-200 border-amber-700';
  return 'bg-green-900/50 text-green-200 border-green-700';
}

const EMPTY = { description: '', risk_type: RISK_TYPES[0], likelihood: 3, impact: 3, treatment: 'mitigate', owner: '', review_date: '' };

export function RisksTab({ engagementId, reload }: { engagementId: string; reload: () => Promise<void> }) {
  const [risks, setRisks] = useState<Risk[] | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    try {
      const r = await api.get<{ risks: Risk[] }>(`/api/engagements/${engagementId}/risks`);
      setRisks(r.risks);
    } catch {
      setError('Failed to load risks.');
    }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [engagementId]);

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/api/engagements/${engagementId}/risks`, form);
      setForm({ ...EMPTY });
      setShowForm(false);
      await load();
      await reload();
    } catch {
      setError('Failed to add risk.');
    } finally {
      setBusy(false);
    }
  }

  async function patchRisk(r: Risk, fields: Record<string, unknown>) {
    await api.patch(`/api/risks/${r.id}`, fields);
    await load();
    await reload();
  }

  async function remove(r: Risk) {
    if (!confirm('Delete this risk?')) return;
    await api.del(`/api/risks/${r.id}`);
    await load();
    await reload();
  }

  if (!risks) return <Spinner />;

  return (
    <div>
      <SectionTitle
        right={<button className="btn-primary !px-3 !py-1.5 text-xs" onClick={() => setShowForm((v) => !v)}>+ Add risk</button>}
      >
        Risk register
      </SectionTitle>
      {error && <ErrorNote message={error} />}

      {showForm && (
        <form onSubmit={create} className="card mb-4 space-y-3">
          <div>
            <label className="label">Risk description</label>
            <textarea className="input min-h-16" required minLength={5} value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="e.g. Support copilot may expose other customers' data via retrieval misconfiguration" />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
            <div>
              <label className="label">Type</label>
              <select className="input" value={form.risk_type} onChange={(e) => setForm((f) => ({ ...f, risk_type: e.target.value }))}>
                {RISK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Likelihood (1–5)</label>
              <select className="input" value={form.likelihood} onChange={(e) => setForm((f) => ({ ...f, likelihood: Number(e.target.value) }))}>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Impact (1–5)</label>
              <select className="input" value={form.impact} onChange={(e) => setForm((f) => ({ ...f, impact: Number(e.target.value) }))}>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Treatment</label>
              <select className="input" value={form.treatment} onChange={(e) => setForm((f) => ({ ...f, treatment: e.target.value }))}>
                {TREATMENTS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Owner</label>
              <input className="input" value={form.owner} onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))} />
            </div>
            <div>
              <label className="label">Review date</label>
              <input className="input" type="date" value={form.review_date} onChange={(e) => setForm((f) => ({ ...f, review_date: e.target.value }))} />
            </div>
          </div>
          <button className="btn-primary" disabled={busy}>{busy ? 'Adding…' : 'Add risk'}</button>
        </form>
      )}

      {risks.length === 0 ? (
        <div className="card py-12 text-center text-sm text-slate-500">
          No risks recorded. Each risk gets owner, likelihood × impact, treatment, residual risk, and a review date.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full">
            <thead className="bg-slate-900">
              <tr>
                <th className="th">Severity</th>
                <th className="th">Risk</th>
                <th className="th">Treatment</th>
                <th className="th">Owner</th>
                <th className="th">Residual</th>
                <th className="th">Review</th>
                <th className="th">Status</th>
                <th className="th"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {risks.map((r) => {
                const sev = r.likelihood * r.impact;
                return (
                  <tr key={r.id} className="bg-slate-950/40">
                    <td className="td">
                      <span className={`chip ${sevColor(sev)}`}>{sev} <span className="opacity-70">(L{r.likelihood}×I{r.impact})</span></span>
                    </td>
                    <td className="td">
                      <div className="font-medium text-slate-100">{r.description}</div>
                      <div className="text-xs text-slate-500">
                        {r.risk_type ?? '—'}{r.use_case_name ? ` · UC: ${r.use_case_name}` : ''}
                      </div>
                    </td>
                    <td className="td">
                      <select className="input !w-28 !py-1 text-xs" value={r.treatment} onChange={(e) => void patchRisk(r, { treatment: e.target.value })}>
                        {TREATMENTS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td className="td text-xs">{r.owner ?? '—'}</td>
                    <td className="td">
                      <input className="input !w-24 !py-1 text-xs" defaultValue={r.residual_risk ?? ''} placeholder="low/med/high"
                             onBlur={(e) => { if (e.target.value !== (r.residual_risk ?? '')) void patchRisk(r, { residual_risk: e.target.value }); }} />
                    </td>
                    <td className="td text-xs">{r.review_date ?? '—'}</td>
                    <td className="td">
                      <select className="input !w-24 !py-1 text-xs" value={r.status} onChange={(e) => void patchRisk(r, { status: e.target.value })}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="td">
                      <button className="text-xs text-slate-600 hover:text-red-400" onClick={() => void remove(r)}>✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
